class CartDrawer extends HTMLElement {
  constructor() {
      super();

      this.addEventListener(
          "keyup",
          (evt) => evt.code === "Escape" && this.close()
      );
      this.querySelector("#CartDrawer-Overlay").addEventListener(
          "click",
          this.close.bind(this)
      );
      this.setHeaderCartIconAccessibility();

      // Listen for reinitialization events from cart recommendations
      this.addEventListener('cart-drawer:reinitialize', this.handleReinitialize.bind(this));

      // Initialize cart timer state
      if (!window.cartTimerState) {
          window.cartTimerState = {
              initialized: false,
              interval: null,
              minutes: 0,
              seconds: 0,
          };
      }
  }

  setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector("#cart-icon-bubble");
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.addEventListener("click", (event) => {
          event.preventDefault();
          this.open(cartLink);
      });
      cartLink.addEventListener("keydown", (event) => {
          if (event.code.toUpperCase() === "SPACE") {
              event.preventDefault();
              this.open(cartLink);
          }
      });
  }

  open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
          this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
          this.classList.add("animate", "active");
      });

      this.addEventListener(
          "transitionend",
          () => {
              const containerToTrapFocusOn = this.classList.contains(
                  "is-empty"
              )
                  ? this.querySelector(".drawer__inner-empty")
                  : document.getElementById("CartDrawer");
              const focusElement =
                  this.querySelector(".drawer__inner") ||
                  this.querySelector(".drawer__close");
              trapFocus(containerToTrapFocusOn, focusElement);
          },
          { once: true }
      );

      document.body.classList.add("overflow-hidden");

      // Initialize or update the cart expiration timer
      this.setupExpirationTimer();
  }

  // New method to handle the cart expiration timer
  setupExpirationTimer() {
      // Check if cart is empty, don't show timer if empty
      if (this.classList.contains("is-empty")) {
          return;
      }

      // Get the settings from data attributes
      const hideTimer = this.getAttribute("data-hide-timer") === "true";
      const timerText =
          this.getAttribute("data-timer-text") ||
          "Please note: your shopping cart will expire in";

      if (hideTimer) {
          // Remove timer if it exists but setting is disabled
          const existingTimer = document.getElementById(
              "cart-expiration-message"
          );
          if (existingTimer) {
              existingTimer.remove();
          }
          return;
      }

      // Create the timer message if it doesn't exist
      this.createTimerElement(timerText);

      // Get the countdown element
      const countdownEl = document.getElementById("cart-countdown");
      if (!countdownEl) return;

      // Initialize with random time if not already set
      if (!window.cartTimerState.initialized) {
          // Generate random minutes between 10 and 30
          // const randomMinutes = Math.floor(Math.random() * 21) + 10; // 10 to 30 min
          window.cartTimerState.minutes = 10;
          window.cartTimerState.seconds = 0;
          window.cartTimerState.initialized = true;
      }

      // Update display immediately
      this.updateCountdownDisplay(countdownEl);

      // Clear any existing interval
      if (window.cartTimerState.interval) {
          clearInterval(window.cartTimerState.interval);
      }

      // Set new interval
      window.cartTimerState.interval = setInterval(() => {
          this.updateCountdown(countdownEl);
      }, 1000);
  }

  // Create the timer element if it doesn't exist
  createTimerElement(timerText) {
      // Check if the timer message already exists
      if (document.getElementById("cart-expiration-message")) {
          return;
      }

      // Create timer message element
      const timerElement = document.createElement("div");
      timerElement.id = "cart-expiration-message";
      timerElement.className = "cart-expiration-message";

      timerElement.innerHTML = `
  <svg style="margin-right: 10px; width: 20px; height: 20px;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="black">
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z"/>
  </svg>
  <span>${timerText} <span id="cart-countdown">00:00</span></span>
`;

      // Find where to insert the timer (after shipping message if exists, otherwise after header)
      const shippingMessage = this.querySelector(".cart-shipping");
      const drawerHeader = this.querySelector(".drawer__header");

      if (shippingMessage) {
          shippingMessage.after(timerElement);
      } else if (drawerHeader) {
          drawerHeader.after(timerElement);
      }
  }

  // Update the countdown timer
  updateCountdown(countdownEl) {
      if (window.cartTimerState.seconds === 0) {
          if (window.cartTimerState.minutes === 0) {
              clearInterval(window.cartTimerState.interval);
              window.cartTimerState.interval = null;
              return;
          }
          window.cartTimerState.minutes--;
          window.cartTimerState.seconds = 59;
      } else {
          window.cartTimerState.seconds--;
      }

      this.updateCountdownDisplay(countdownEl);
  }

  // Update the countdown display
  updateCountdownDisplay(countdownEl) {
      // Format the time
      const formattedMinutes = window.cartTimerState.minutes
          .toString()
          .padStart(2, "0");
      const formattedSeconds = window.cartTimerState.seconds
          .toString()
          .padStart(2, "0");

      countdownEl.textContent = `${formattedMinutes}:${formattedSeconds}`;
  }

  close() {
      this.classList.remove("active");
      removeTrapFocus(this.activeElement);
      document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute("role", "button");
      cartDrawerNote.setAttribute("aria-expanded", "false");

      if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
          cartDrawerNote.setAttribute(
              "aria-controls",
              cartDrawerNote.nextElementSibling.id
          );
      }

      cartDrawerNote.addEventListener("click", (event) => {
          event.currentTarget.setAttribute(
              "aria-expanded",
              !event.currentTarget.closest("details").hasAttribute("open")
          );
      });

      cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
      this.querySelector(".drawer__inner").classList.contains("is-empty") &&
          this.querySelector(".drawer__inner").classList.remove("is-empty");
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
          sectionElement.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.id],
              section.selector
          );
      });

      setTimeout(() => {
          this.querySelector("#CartDrawer-Overlay").addEventListener(
              "click",
              this.close.bind(this)
          );
          this.open();
      });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
      return new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
      return [
          {
              id: "cart-drawer",
              selector: "#CartDrawer",
          },
          {
              id: "cart-icon-bubble",
          },
      ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
      return new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector(selector);
  }

  setActiveElement(element) {
      this.activeElement = element;
  }

  handleReinitialize(event) {
      // Re-setup cart drawer event listeners after content update
      const overlay = this.querySelector("#CartDrawer-Overlay");
      if (overlay) {
          // Remove existing listeners and re-add
          overlay.removeEventListener("click", this.close.bind(this));
          overlay.addEventListener("click", this.close.bind(this));
      }
      
      // Re-setup cart drawer items functionality
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
          this.reinitializeCartDrawerItems(cartDrawerItems);
      }
      
      // Re-setup cart expiration timer if needed
      if (!this.classList.contains("is-empty")) {
          this.setupExpirationTimer();
      }
  }

  reinitializeCartDrawerItems(cartDrawerItems) {
      // Re-setup quantity change handlers
      const quantityInputs = cartDrawerItems.querySelectorAll('input[name="quantity"]');
      quantityInputs.forEach(input => {
          // Remove existing listeners
          input.removeEventListener('change', cartDrawerItems.onChange.bind(cartDrawerItems));
          // Re-add listeners
          input.addEventListener('change', cartDrawerItems.onChange.bind(cartDrawerItems));
      });
      
      // Re-setup remove button handlers
      const removeButtons = cartDrawerItems.querySelectorAll('cart-remove-button');
      removeButtons.forEach(button => {
          // Remove existing listeners
          button.removeEventListener('click', (event) => {
              event.preventDefault();
              cartDrawerItems.updateQuantity(button.dataset.index, 0);
          });
          // Re-add listeners
          button.addEventListener('click', (event) => {
              event.preventDefault();
              cartDrawerItems.updateQuantity(button.dataset.index, 0);
          });
      });
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
      return [
          {
              id: "CartDrawer",
              section: "cart-drawer",
              selector: ".drawer__inner",
          },
          {
              id: "cart-icon-bubble",
              section: "cart-icon-bubble",
              selector: ".shopify-section",
          },
      ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
